import { Typography } from "antd-v5";
import AppLoader from "components/AppLoader";
import InternalLink from "components/InternalLink";
import { getVocabulary } from "components/Vocabulary";
import usePaginationControls from "hooks/usePaginationControls";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";
import { compact, find, isEmpty, map, noop } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ExceptionAPI from "restAPI/ExceptionAPI";
import { FindingSummary } from "types/api/findingSummary";
import { RiskRegisterListingResponseDto } from "types/api/riskRegisterListingResponseDto";
import { formatNumberWithCommas } from "utils/tableUtils";
import useExceptionColumns from "views/Inventory/Exception/useExceptionColumns";

import ACLink from "ac-components/ACLink/ACLink";
import ACPaginationBar from "ac-components/ACPaginationBar/ACPaginationBar";
import ACTable, { ACTableColumnType } from "ac-components/ACTable/ACTable";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";

export default function FindingExceptionDetails({
  finding,
}: {
  finding: FindingSummary;
}) {
  const [dataSource, setDataSource] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const cancelRef = useRef(noop);
  const exceptionTablePageControls = usePaginationControls();
  const columns = useExceptionColumns();

  const fetchData = useCallback(() => {
    cancelRef.current();
    setLoading(true);
    const exceptionsIds = map(finding?.exceptions, (exception) => exception.id);
    const { promise, cancel } = ExceptionAPI.getExceptions({
      page: exceptionTablePageControls.page - 1,
      size: exceptionTablePageControls.pageSize,
      filters: { id: exceptionsIds },
    });
    promise
      .then((response) => {
        setDataSource(response.content!);
        setTotalElements(response.totalElements!);
      })
      .catch(() => {
        setDataSource([]);
      })
      .finally(() => {
        setLoading(false);
      });

    cancelRef.current = cancel;
    return cancel;
  }, [
    finding?.exceptions,
    exceptionTablePageControls.page,
    exceptionTablePageControls.pageSize,
  ]);

  useEffect(() => {
    if (!isEmpty(finding?.exceptions)) {
      fetchData();
    } else {
      // When there are no exceptions, clear the data source
      setDataSource([]);
    }
  }, [fetchData, finding?.exceptions]);

  const columnKeys = [
    "name",
    "startDate",
    "endDate",
    "reasons",
    "createdBy",
    "approvedOn",
    "submittedOn",
    "status",
  ];

  const exceptionColumns = compact(
    columnKeys.map((key: string) => {
      return find(columns, { key });
    }),
  );
  const idColumns: ACTableColumnType<RiskRegisterListingResponseDto> = {
    title: "ID",
    dataIndex: "id",
    width: 120,
    key: "id",
    render: (value, record) => (
      <InternalLink
        to={`/exceptions/${value}`}
        target="_blank"
        component={(props) => <ACLink {...props} />}
      >
        EX-{value}
      </InternalLink>
    ),
  };
  const finalColumns = [
    idColumns,
    ...exceptionColumns,
  ] as ACTableColumnType<RiskRegisterListingResponseDto>[];

  const { columns: customizedColumns } =
    useTableCustomizationHelper<RiskRegisterListingResponseDto>({
      columns: finalColumns,
      prefKey: "finding-details-exception-table",
      skipColumnVisibility: true,
      draggable: false,
    });

  if (!finding) {
    return <AppLoader />;
  }

  return (
    <>
      <ACTable<RiskRegisterListingResponseDto>
        autoEnableScroll
        computeTableWidth
        rightScrollShadow
        columns={customizedColumns}
        dataSource={dataSource}
        pagination={{ pageSize: exceptionTablePageControls.pageSize }}
        loading={loading}
        components={{
          header: {
            cell: ACResizableColumnCell,
          },
        }}
      />
      <ACPaginationBar
        size="md"
        total={totalElements}
        page={exceptionTablePageControls.page}
        onChange={exceptionTablePageControls.onPageChange}
        pageSize={exceptionTablePageControls.pageSize}
        showEdges={false}
        message={
          <Typography.Text type="secondary">
            {formatNumberWithCommas(totalElements)} {getVocabulary("exception")}
            {totalElements === 1 ? "" : "s"}
          </Typography.Text>
        }
      />
    </>
  );
}
